import { useEffect, useRef } from 'react'
import { getMessage } from '../../utils/messageUtils'

import './Popup.scss'
import './PopUpForm.scss'
interface ExtraButton {
  label: string
  className?: string
  onClick: () => void
}

interface PopupProps {
  isOpen: boolean
  message?: string
  children?: React.ReactNode
  extraButtons?: ExtraButton[]
  isMobile: boolean
  onOk: (() => void) | undefined
  onClose: () => void
  hideCancelButton?: boolean
}

function Popup({
  isOpen,
  message,
  children,
  extraButtons,
  isMobile,
  onOk,
  onClose,
  hideCancelButton,
}: PopupProps) {
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && onClose) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  useEffect(() => {
    if (isOpen && contentRef.current) {
      // Find the first focusable element (textarea, input, etc.)
      const focusableElement = contentRef.current.querySelector<HTMLElement>(
        'textarea, input, select, button, [tabindex]:not([tabindex="-1"])'
      )
      if (focusableElement) {
        // Small delay to ensure DOM is ready
        setTimeout(() => {
          focusableElement.focus()
          // Move cursor to end if it's a text input or textarea with existing text
          if (
            (focusableElement instanceof HTMLInputElement ||
              focusableElement instanceof HTMLTextAreaElement) &&
            focusableElement.value
          ) {
            const length = focusableElement.value.length
            focusableElement.setSelectionRange(length, length)
          }
        }, 100)
      }
    }
  }, [isOpen])

  if (!isOpen) return null

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && onClose) {
      onClose()
    }
  }

  const handleOkClick = () => {
    if (onOk) {
      onOk()
    }
  }

  const generateExtraButtons = () => {
    if (!extraButtons) return null
    return extraButtons.map((button, index) => (
      <button
        key={index}
        className={`popup-button ${button.className || ''}`}
        onClick={button.onClick}
      >
        {button.label}
      </button>
    ))
  }

  const generateButtons = () => {
    return (
      <div
        className={`popup-buttons-row ${isMobile ? 'popup-buttons-row-mobile' : ''}`}
      >
        {!hideCancelButton && (
          <button
            className="popup-button popup-button-cancel"
            onClick={onClose}
          >
            {getMessage('cancel')}
          </button>
        )}
        {generateExtraButtons()}
        <button
          className="popup-button popup-button-ok"
          onClick={handleOkClick}
        >
          {getMessage('ok')}
        </button>{' '}
      </div>
    )
  }

  return (
    <div className="popup-backdrop" onClick={handleBackdropClick}>
      <div className="popup-container">
        <div className="popup-content" ref={contentRef}>
          {message && <p className="popup-message">{message}</p>}
          {children && <div className="popup-custom-content">{children}</div>}
        </div>
        {generateButtons()}
      </div>
    </div>
  )
}

export default Popup

export type { ExtraButton }
